// Todo: Move some effects to sagas instead of useEffect
import { startCase } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { fetchAppRatingsFiltersData, fetchCockpitFiltersData, setParameters, setSpecificParameter } from "../../actions";
import "../../assets/styles/component/filters.scss";
import { ALL_COUNTRIES, ALL_OPTION_NO_SPACE, DEFAULT_APP_RATINGS_PARAMETERS } from "../../constants";
import { allAppRatingsServicesTitle, defaultAppRatingsServicesTitle, europeAppRatingsServicesTitle } from "../../constants/ccsConstants";
import { FilterContext } from "../../context";
import { checkConsecutiveMonths } from "../../pages/CCS Vital Signs/subs/utils";
import { groupByKeys } from "../../utils/computeFunctions";
import { FY_TWENTY } from "../../utils/dateFunctions";
import { eventTracking, MixpanelEvents } from "../../utils/userTracking";
import { FiltersLayout } from "../Layouts";
import { DefaultFilter, FilterBtns, MultiDateRangeWithPresets, MultiSelectFilter } from "./common";
import { AppRatingsBreakdownGeoFilter } from "./common/AppRatingsBreakdownGeoFilter";
import { closeFilterOptions, closeMarketOptions } from "./subs/helpers";

interface Props {
  filterBtns: Array<{ id: string; navigateTo: string; name: string }>;
}

const APP_STORE_FILTER_OPTIONS = ["All", "Android", "iOS"];

export const CcsAppRatingsBreakdownFilters = withRouter((props: Props & RouteComponentProps) => {
  const { history, filterBtns } = props;

  const dispatch = useDispatch();

  const { showFilterOptions, setShowFilterOptions } = useContext(FilterContext);

  const dateRangeParamValue = useSelector((state: RootStateOrAny) => state.parameters.date_range);
  const brandParamValue = useSelector((state: RootStateOrAny) => state.parameters.brand);
  const regionParamValue = useSelector((state: RootStateOrAny) => state.parameters.region);
  const marketParamValue = useSelector((state: RootStateOrAny) => state.parameters.market);
  const appParamValue = useSelector((state: RootStateOrAny) => state.parameters.app);
  const appStoreParamValue = useSelector((state: RootStateOrAny) => state.parameters.app_store);

  const { date_range } = useSelector((state: RootStateOrAny) => state.filters.app_ratings_filters);
  const { user_engagement_geography } = useSelector((state: RootStateOrAny) => state.filters.cockpit_data);

  const [marketOptionsVisible, setMarketOptionsVisible] = useState(false);

  const marketList = useMemo(() => {
    if (user_engagement_geography) {
      const regionGeos = regionParamValue == "Europe" ? user_engagement_geography.filter((geo: any) => geo.region == "europe") : user_engagement_geography.filter((geo: any) => geo.region == "asean");
      return regionGeos?.reduce((result: Array<string>, geo: any) => {
        const { market } = geo;
        return [...result, startCase(market)];
      }, []);
    }
    return [];
  }, [user_engagement_geography, regionParamValue]);

  const [dateParam, setDateParam] = useState<string>(dateRangeParamValue);
  const [regionParam, setRegionParam] = useState<string>(regionParamValue);
  const [marketParam, setMarketParam] = useState<string>(marketParamValue);

  // const [marketList, setMarketList] = useState<Array<string>>([]);

  // Filter input values
  const [dateRangeFilterValue, setDateRangeFilterValue] = useState<string>("");
  const [appStoreValue, setAppStoreValue] = useState<string>("");
  const [appValue, setAppValue] = useState<string>("");
  const [brandValue, setBrandValue] = useState<string>("");
  const [geographyValue, setGeographyValue] = useState<string>("");

  const [servicesTitle, setServicesTitle] = useState(defaultAppRatingsServicesTitle);

  const [APP_OPTIONS, setAppOptions] = useState(["All", "NissanConnect EV", servicesTitle]);
  const BRAND_OPTIONS = ["All", "Nissan", "Infiniti"];

  useEffect(() => {
    if (regionParamValue) {
      /all/gi.test(regionParamValue)
        ? setServicesTitle(allAppRatingsServicesTitle)
        : regionParamValue === "Europe"
          ? setServicesTitle(europeAppRatingsServicesTitle)
          : setServicesTitle(defaultAppRatingsServicesTitle);
    }
  }, [regionParamValue]);

  useEffect(() => setAppOptions(["All", "NissanConnect EV", servicesTitle]), [servicesTitle]);

  useEffect(() => {
    if (regionParamValue == "Europe") {
      dispatch(setSpecificParameter("market", "G8"));
    } else {
      dispatch(setSpecificParameter("market", "All"));
    }
  }, [regionParamValue]);

  // Resets invalid ccs filter values
  useEffect(() => {
    Promise.all([dispatch(fetchAppRatingsFiltersData()), dispatch(fetchCockpitFiltersData())]);
  }, [dispatch]);

  const groupedDateRangeData = useMemo(() => {
    const groupedDate = groupByKeys(date_range, "year");
    return Object.keys(groupedDate).map((year) => ({ year, child: groupedDate[year] }));
  }, [date_range]);

  // Updates date range filter value
  useEffect(() => {
    if (date_range) {
      // Todo: Updates date range values
      const selectedDateArray: Array<string> = dateRangeParamValue?.split(",");
      const availableDateOptions: Array<string> = date_range?.map(
        (dates: { year: number; month: string }) => `${dates.month} ${dates.year}`
      );

      const isFY20 = selectedDateArray.length === FY_TWENTY.length && selectedDateArray.every((dateValue) => FY_TWENTY.includes(dateValue));

      const checkConsecutive = checkConsecutiveMonths(selectedDateArray, availableDateOptions);

      const dateRangeInputValue = isFY20
        ? "FY20"
        : selectedDateArray.length > 1
          ? checkConsecutive.isConsecutive
            ? checkConsecutive.consecutiveDateRange
            : `${selectedDateArray?.length} months selected`
          : dateRangeParamValue;

      const isValidCCSDate: boolean = selectedDateArray.reduce(
        (result: boolean, date) => result && (date == ALL_OPTION_NO_SPACE ? true : availableDateOptions.includes(date)),
        true
      );

      const availableCalendarYears = Array.from(new Set(date_range.map((option: { month: string; year: number }) => option.year)));

      // Sets the default date selection to the latest available calendar year
      if (!isValidCCSDate) {
        const defaultSelection = availableDateOptions.filter((option) => {
          const calendarYearRe = new RegExp(`${availableCalendarYears[availableCalendarYears.length - 1]}`, "gi");

          return calendarYearRe.test(option);
        });

        dispatch(setSpecificParameter("date_range", defaultSelection.join(",")));
        dispatch(setSpecificParameter("dates", defaultSelection.join(",")));
      }
      setDateParam(dateRangeParamValue);
      setDateRangeFilterValue(dateRangeInputValue);
    }
  }, [dateRangeParamValue, date_range]);

  useEffect(() => {
    setRegionParam(regionParamValue);
  }, [regionParamValue]);

  useEffect(() => {
    setMarketParam(marketParamValue);
  }, [marketParamValue]);

  // Set app filter value
  useEffect(() => {
    const value = appParamValue
      ? appParamValue.includes("EV")
        ? "NissanConnect EV"
        : appParamValue.includes("Service")
          ? servicesTitle
          : ALL_OPTION_NO_SPACE
      : ALL_OPTION_NO_SPACE;
    setAppValue(value);
  }, [appParamValue]);

  useEffect(() => {
    if (marketList.length > 0 && marketParamValue) {
      const selectedGeosArr = marketParamValue.split(",");
      const marketNameValue =
        selectedGeosArr?.length === marketList.length
          ? "All"
          : selectedGeosArr?.length > 1
            ? `${selectedGeosArr?.length} markets selected`
            : marketParamValue;

      setGeographyValue(marketNameValue);
    }
  }, [marketList, marketParamValue]);

  // Set params
  useEffect(() => {
    const params = new URLSearchParams();

    params.set("date_range", dateParam);
    params.set("brand", brandParamValue);
    params.set("region", regionParam);
    params.set("market", marketParam);
    params.set("app_store", appStoreParamValue);
    params.set("app", appParamValue);
    history.push({ search: `?${params.toString()}` });
  }, [dateParam, appParamValue, regionParam, marketParam, appStoreParamValue, brandParamValue, history]);

  const showFilterOptionsFn = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      const {
        currentTarget: { dataset },
      } = evt;
      const optionsId = dataset?.optionsUl;
      const shownClass = "showOptions";

      const optionsElement = optionsId ? document.getElementById(optionsId) : undefined;

      if (showFilterOptions && optionsElement?.classList.contains(shownClass)) {
        closeFilterOptions();
        closeMarketOptions();
        setShowFilterOptions(false);
      } else {
        closeFilterOptions();
        optionsElement?.classList.toggle(shownClass);
        setShowFilterOptions(!showFilterOptions);
      }
    },
    [showFilterOptions, setShowFilterOptions]
  );

  const resetFilters = useCallback(() => {
    eventTracking(MixpanelEvents.filter_reset, { dashboard: "CCS (archived)", page: location.pathname });
    dispatch(setParameters(DEFAULT_APP_RATINGS_PARAMETERS));
  }, [history, location]);

  const handleFilterOptionClick = useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      const {
        currentTarget: { dataset },
      } = evt;
      const filterName = dataset?.filter;
      const optionValue = dataset?.value;

      if (filterName && optionValue) {
        eventTracking(MixpanelEvents.filter_change, { filter: filterName, value: optionValue, dashboard: "CCS (archived)", page: "CCS App ratings (archived)" });
        if (filterName === "brand") {
          Promise.all([dispatch(setSpecificParameter(filterName, optionValue)), dispatch(setSpecificParameter("market", ALL_COUNTRIES))]);
        } else if (filterName === "app") {
          if (optionValue.includes("EV")) {
            dispatch(setSpecificParameter(filterName, "EV"));
          } else if (optionValue.includes("Service")) {
            dispatch(setSpecificParameter(filterName, "Service"));
          } else {
            dispatch(setSpecificParameter(filterName, ALL_OPTION_NO_SPACE));
          }
        } else {
          dispatch(setSpecificParameter(filterName, optionValue));
        }
      }
      closeFilterOptions();
      closeMarketOptions();
      setShowFilterOptions(false);
    },
    [setShowFilterOptions, dispatch]
  );

  const handleDateFilterSubmission = () => {
    const parameterName = "date_range";
    const selectedCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="${parameterName}"][data-parent-value]:checked`
    ) as NodeListOf<HTMLInputElement>;

    const selectedValues: Array<string> = [];
    selectedCheckboxes?.forEach((checkbox) => {
      if (!/^\d+$/.test(checkbox.value)) selectedValues.push(checkbox.value);
    });

    closeFilterOptions();
    const value = selectedValues.join(",");
    dispatch(setSpecificParameter(parameterName, value));
    eventTracking(MixpanelEvents.filter_change, { filter: parameterName, value, dashboard: "CCS (archived)", page: "CCS App ratings (archived)" });
  };

  useEffect(() => {
    setBrandValue(brandParamValue);
  }, [brandParamValue]);

  // Set app store filter value
  useEffect(() => {
    const value = appStoreParamValue ? appStoreParamValue : ALL_OPTION_NO_SPACE;
    setAppStoreValue(value);
  }, [appStoreParamValue]);

  const handleMarketFilterSubmission = useCallback(() => {
    const selectedModelCheckboxes = document.querySelectorAll(
      `[type="checkbox"][data-filter-name="market"][data-options-type="single"]:checked`
    ) as NodeListOf<HTMLInputElement>;
    const selectedModels: Array<string> = [];
    selectedModelCheckboxes?.forEach((checkbox) => selectedModels.push(checkbox.value));

    closeFilterOptions();
    const value = marketList?.length === selectedModels?.length ? "All" : selectedModels.join(",");
    dispatch(setSpecificParameter("market", value));
  }, [marketList]);

  return (
    <FiltersLayout resetFilters={resetFilters}>
      <>
        {/* Date range */}
        <MultiDateRangeWithPresets
          parentKey={"year"}
          childKey={"month"}
          filterName="DATE RANGE"
          // @ts-ignore
          filterList={groupedDateRangeData}
          value={dateRangeFilterValue}
          parameterName={"date_range"}
          parameterValue={dateRangeParamValue}
          onShowFilterOptions={showFilterOptionsFn}
          handleFilterSubmission={handleDateFilterSubmission}
        />

        {/* App */}
        <DefaultFilter
          list={BRAND_OPTIONS}
          filterName={"brand"}
          filterLabel={"BRAND"}
          filterValue={brandValue}
          handleFilterOptionClick={handleFilterOptionClick}
        />

        {/* Geography */}
        {regionParamValue == "Europe" ? (
          <AppRatingsBreakdownGeoFilter
            filterValue={geographyValue}
            marketList={marketList}
            marketParamValue={marketParamValue}
            showFilterOptions={showFilterOptionsFn}
          />
        ) : (
          <MultiSelectFilter
            parentKey="market"
            // @ts-ignore
            filterList={marketList}
            filterName="GEOGRAPHY"
            value={geographyValue}
            parameterName={"market"}
            parameterValue={marketParamValue}
            onShowFilterOptions={showFilterOptionsFn}
            handleFilterSubmission={handleMarketFilterSubmission}
          />
        )}

        {/* App */}
        <DefaultFilter
          list={APP_OPTIONS}
          filterName={"app"}
          filterLabel={"APP"}
          filterValue={appValue}
          handleFilterOptionClick={handleFilterOptionClick}
        />

        {/* App store */}
        <DefaultFilter
          list={APP_STORE_FILTER_OPTIONS}
          filterName={"app_store"}
          filterLabel={"OPERATING SYSTEM"}
          filterValue={appStoreValue}
          handleFilterOptionClick={handleFilterOptionClick}
        />

        {filterBtns.map((btn) => (
          <FilterBtns id={btn.id} navigateTo={btn.navigateTo} name={btn.name} key={btn.id} />
        ))}
      </>
    </FiltersLayout>
  );
});
